import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { THEMES } from '../../data/product/product.data';
import { AnalyticsService } from '../analytics/analytics.service';

@Injectable( {
  providedIn: 'root',
} )
export class MonitorProviderService {

  constructor( private http: HttpClient, private analyticsService: AnalyticsService ) {
    // Intentionally blank
  }

  getStats( productKey: string ) {
    return this.http.get<any[]>( `${ environment.serverUrl }/monitor/stats`, { params: { productKey } } );
  }

  /**
   * Fetches stats for every product in a single request, grouped by product
   * key ( `{ [productKey]: platformStats[] }` ). Used by listing pages
   * ( home / themes ); single-theme pages keep using `getStats`.
   */
  getAllStats() {
    return this.http.get<{ [productKey: string]: any[] }>( `${ environment.serverUrl }/monitor/stats/all` );
  }

  /**
   * The newest marketplace sync across every stats row ( `{ syncedAt }`, ISO
   * or null before the first sync ). The footer shows it as "updated N ago"
   * next to the site version; one tiny request instead of /stats/all.
   */
  getLastSync() {
    return this.http.get<{ syncedAt: string | null }>( `${ environment.serverUrl }/monitor/last-sync` );
  }

  recordClick( productKey: string, platform: string ) {
    // The single chokepoint for every marketplace CTA ("Go to store" / "Download now")
    // across the theme page, home banner and themes-grid expander. Mirror it to
    // Mixpanel as the key purchase-intent signal: which theme + platform a
    // visitor clicks through on.
    this.analyticsService.logEvent( 'Theme CTA Clicked', { theme: this.themeName( productKey ), platform } );
    return this.http.post( `${ environment.serverUrl }/monitor/click`, { productKey, platform } );
  }

  recordDownload( productKey: string, platform: string, options: { mirrorToAnalytics?: boolean } = {} ) {
    // Fired when a visitor copies/downloads a theme file from the in-app
    // theme/content page - the completed-download counterpart to the CTA click.
    // Non-theme callers (palettes) log their own, correctly named Mixpanel
    // event and pass `mirrorToAnalytics: false` so a palette download never
    // shows up as "Theme Downloaded".
    if ( options.mirrorToAnalytics !== false ) {
      this.analyticsService.logEvent( 'Theme Downloaded', { theme: this.themeName( productKey ), platform } );
    }
    return this.http.post( `${ environment.serverUrl }/monitor/download`, { productKey, platform } );
  }

  // The proper, capitalised theme NAME for a product key, so Mixpanel shows
  // "Aurora Borealis" rather than the raw "aurora-borealis" key (and matches the
  // name the themes grid already sends). Falls back to a Title Case of the key.
  private themeName( productKey: string ): string {
    for ( const line of THEMES ) {
      const product = line.products.find( p => p.key === productKey );
      if ( product?.name ) {
        return product.name;
      }
    }
    return ( productKey || '' )
      .replace( /[-_]+/g, ' ' )
      .trim()
      .replace( /\b\w/g, c => c.toUpperCase() );
  }
}
